import { Button, Card, DailyTradeChart, TradeChart } from '@features/ui';
import { useTranslation } from 'react-i18next';
import { PaymentIcon } from '@features/ui/icons';
import {
  ChartInformation,
  DropdownAccount,
  // CredentialsDialog,
} from '@features/user/dashboard';
import { TOption } from '@src/types/dropdown.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ViewButton } from '@features/ui/components/view-button.tsx';
import {
  getDailyTradeVolumesAndContracts,
  getDailyProfitLossAndBalance,
} from '@src/services/payout.ts';
import { StackedData, BalanceStackedData } from '@src/types';
import { getMyTradingAccounts } from '@src/services/users';
import { LeelooAccounts } from '@src/types/users';
import { useAuthStore } from '@src/stores/authStore';
import {
  fetchPurchaseResetPackage,
  submitPurchaseResetCheckout,
} from '@src/services/purchaseReset';
import { PurchaseResetPackage } from '@src/types/purchaseReset';
import { PaymentFormValues } from '@src/types/packages';
import { toast } from 'react-toastify';
import { CLOSED_AUTOLIQUIDATE_STATUS } from '@src/constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { PurchaseResetDialog } from './PurchaseResetDialog';

dayjs.extend(utc);

export const Analytics = () => {
  const { t } = useTranslation();

  const [hideChart, setHideChart] = useState(false);
  // const [openDialog, setOpenDialog] = useState(false);
  const [tradeData, setTradeData] = useState<StackedData[]>([]);
  const [balanceData, setBalanceData] = useState<BalanceStackedData[]>([]);
  const [myAccounts, setMyAccounts] = useState<LeelooAccounts[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [purchaseResetOpen, setPurchaseResetOpen] = useState(false);
  const [purchaseResetLoading, setPurchaseResetLoading] = useState(false);
  const [purchaseResetSubmitting, setPurchaseResetSubmitting] = useState(false);
  const [purchaseResetPackage, setPurchaseResetPackage] =
    useState<PurchaseResetPackage | null>(null);
  const [leelooUserId, setLeelooUserId] = useState<number | null>(null);

  const { auth } = useAuthStore();
  const isAccountActive = () => {
    if (!auth.user?.competitionActivationDate) {
      return true;
    }

    return dayjs.utc().isAfter(dayjs.utc(auth.user.competitionActivationDate));
  };

  // Convert accounts to dropdown options
  const accountOptions: TOption[] = useMemo(() => {
    return myAccounts.map((account) => ({
      value: account.id?.toString() || '',
      label:
        account.tradovate_account_name ||
        account.rithmic_account_name ||
        `Account ${account.id}`,
    }));
  }, [myAccounts]);

  // Fetch user's accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const accountsRes = await getMyTradingAccounts();

        setMyAccounts(accountsRes.data?.accounts || []);
        setLeelooUserId(accountsRes.data?.id || null);
        auth.setUser({
          ...auth.user,
          createdAt: accountsRes.data?.created_at,
          accountName: accountsRes.data?.username,
          competitionActivationDate:
            accountsRes.data?.competition_activation_date,
        });
        // Auto-select first account if available
        if (
          accountsRes.data?.accounts?.length > 0 &&
          accountsRes.data?.accounts[0].id
        ) {
          setSelectedAccountId(accountsRes.data?.accounts[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };

    setTimeout(() => {
      void fetchAccounts();
    }, 0);
  }, []);

  // Fetch chart data when account is selected
  useEffect(() => {
    const fetchChartData = async () => {
      if (!selectedAccountId) {
        setTradeData([]);
        setBalanceData([]);

        return;
      }

      setLoading(true);
      try {
        const [tradeRes, balanceRes] = await Promise.all([
          getDailyTradeVolumesAndContracts(selectedAccountId),
          getDailyProfitLossAndBalance(selectedAccountId),
        ]);

        setTradeData(tradeRes.data);
        setBalanceData(balanceRes.data);
      } catch (error) {
        console.error('Failed to fetch chart data:', error);
        setTradeData([]);
        setBalanceData([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchChartData();
  }, [selectedAccountId]);

  const handleHideChart = useCallback(() => {
    setHideChart((prev) => !prev);
  }, []);

  const handleAccountChange = useCallback((option: TOption) => {
    setSelectedAccountId(Number(option.value));
  }, []);

  // Get the selected account to pass to ChartInformation
  const selectedAccount = useMemo(() => {
    if (!selectedAccountId) return undefined;

    return myAccounts.find((account) => account.id === selectedAccountId);
  }, [selectedAccountId, myAccounts]);

  const handlePurchaseReset = useCallback(async () => {
    if (!selectedAccountId) {
      toast.error('Please select an account first');

      return;
    }

    setPurchaseResetLoading(true);
    setPurchaseResetOpen(true);

    try {
      const response = await fetchPurchaseResetPackage();

      setPurchaseResetPackage(response.data);
    } catch (error) {
      console.error('Failed to fetch purchase reset quote:', error);
      toast.error('Failed to load purchase reset details. Please try again.');
      setPurchaseResetOpen(false);
    } finally {
      setPurchaseResetLoading(false);
    }
  }, [selectedAccountId]);

  const handlePurchaseResetSubmit = useCallback(
    async (values: PaymentFormValues) => {
      if (!selectedAccountId || !purchaseResetPackage) return;

      setPurchaseResetSubmitting(true);

      try {
        const payload = {
          leeloo_user_id: leelooUserId,
          leeloo_account_id: selectedAccountId,
          payment_gateway: values.payment_type,
          coupon_code: values.coupon_code,
          billing_info: {
            first_name: values.first_name,
            last_name: values.last_name,
            street: values.street,
            city: values.city,
            country: values.country,
            state: values.state,
            zip_code: values.zip_code,
            phone_number: values.phone_number,
          },
        };

        const response = await submitPurchaseResetCheckout(payload);

        if (response.data?.url) {
          window.location.href = response.data.url;
        } else {
          toast.success('Purchase reset completed successfully!');
          setPurchaseResetOpen(false);
          const accountsRes = await getMyTradingAccounts();

          setMyAccounts(accountsRes.data?.accounts || []);
        }
      } catch (error: any) {
        console.error('Failed to submit purchase reset:', error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'Failed to process purchase reset. Please try again.';

        toast.error(errorMessage);
      } finally {
        setPurchaseResetSubmitting(false);
      }
    },
    [selectedAccountId, purchaseResetPackage, leelooUserId]
  );

  if (!isAccountActive()) return null;

  return (
    <div>
      <div className={'flex justify-between items-center'}>
        <div className={'flex gap-x-1 items-center'}>
          <h1 className={'text-2xl font-medium'}>{t('dashboard.analytics')}</h1>
          <DropdownAccount
            defaultValue={selectedAccountId?.toString()}
            options={accountOptions}
            placeHolder="Select Account"
            onPress={handleAccountChange}
          />
          <ViewButton visible={hideChart} onClick={handleHideChart} />
        </div>

        <div className={'flex gap-x-5'}>
          {/*<Button*/}
          {/*  className="[&_svg]:!size-auto"*/}
          {/*  size={'lg'}*/}
          {/*  onClick={() => setOpenDialog(true)}*/}
          {/*>*/}
          {/*  <p>{t('dashboard.credentials')}</p>*/}
          {/*  <KeyIcon />*/}
          {/*</Button>*/}
          {selectedAccount?.status === CLOSED_AUTOLIQUIDATE_STATUS && (
            <Button
              className="[&_svg]:!size-auto"
              size={'lg'}
              onClick={handlePurchaseReset}
            >
              <p>{t('dashboard.purchaseReset')}</p>
              <PaymentIcon />
            </Button>
          )}
        </div>
      </div>
      {!hideChart && (
        <Card className={'mt-5'}>
          <ChartInformation
            accounts={selectedAccount ? [selectedAccount] : []}
          />
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <p className="text-gray-500">Loading data...</p>
            </div>
          ) : (
            <div className={'flex w-full justify-between'}>
              <DailyTradeChart data={tradeData} />
              <TradeChart
                data={balanceData.map((item) => ({
                  date: item.date,
                  accountBalance: item.account_balance,
                  netPl: item.net_pl,
                }))}
              />
            </div>
          )}
        </Card>
      )}
      {/*<CredentialsDialog open={openDialog} onOpenChange={setOpenDialog} />*/}
      <PurchaseResetDialog
        loading={purchaseResetLoading}
        open={purchaseResetOpen}
        resetPackage={purchaseResetPackage}
        submitting={purchaseResetSubmitting}
        onOpenChange={setPurchaseResetOpen}
        onSubmit={handlePurchaseResetSubmit}
      />
    </div>
  );
};
